import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../store';

/**
 * Admin action types (mirrors backend AdminActionType)
 */
type AdminActionType =
  | 'user_view'
  | 'user_list'
  | 'user_enable'
  | 'user_disable'
  | 'user_password_reset'
  | 'user_subscription_change'
  | 'dictionary_view'
  | 'dictionary_list'
  | 'dictionary_add_word'
  | 'dictionary_remove_word'
  | 'dictionary_import'
  | 'dictionary_delete';

/**
 * Admin log entry from the backend
 */
interface AdminLogEntry {
  id: string;
  adminId: string;
  adminEmail?: string;
  targetUserId: string | null;
  targetUserEmail?: string | null;
  actionType: AdminActionType;
  details: Record<string, unknown>;
  ipAddress: string | null;
  createdAt: string;
}

/**
 * Pagination info from the API
 */
interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/**
 * API response for logs endpoint
 */
interface LogsApiResponse {
  success: boolean;
  data: AdminLogEntry[];
  pagination: PaginationInfo;
  error?: { code: string; message: string };
}

/**
 * Filter state for the logs page
 */
interface LogFilters {
  adminId: string;
  targetUserId: string;
  actionType: string;
  startDate: string;
  endDate: string;
}

/**
 * Action type labels in French
 */
const ACTION_TYPE_LABELS: Record<AdminActionType, string> = {
  user_view: 'Consultation utilisateur',
  user_list: 'Liste utilisateurs',
  user_enable: 'Activation compte',
  user_disable: 'Désactivation compte',
  user_password_reset: 'Reset mot de passe',
  user_subscription_change: 'Modification abonnement',
  dictionary_view: 'Consultation dictionnaire',
  dictionary_list: 'Liste dictionnaires',
  dictionary_add_word: 'Ajout mot',
  dictionary_remove_word: 'Suppression mot',
  dictionary_import: 'Import dictionnaire',
  dictionary_delete: 'Suppression dictionnaire',
};

/**
 * Action type styles for badges
 */
const ACTION_TYPE_STYLES: Record<AdminActionType, string> = {
  user_view: 'bg-blue-100 text-blue-700',
  user_list: 'bg-slate-100 text-slate-700',
  user_enable: 'bg-green-100 text-green-700',
  user_disable: 'bg-red-100 text-red-700',
  user_password_reset: 'bg-yellow-100 text-yellow-700',
  user_subscription_change: 'bg-purple-100 text-purple-700',
  dictionary_view: 'bg-blue-100 text-blue-700',
  dictionary_list: 'bg-slate-100 text-slate-700',
  dictionary_add_word: 'bg-green-100 text-green-700',
  dictionary_remove_word: 'bg-red-100 text-red-700',
  dictionary_import: 'bg-purple-100 text-purple-700',
  dictionary_delete: 'bg-red-100 text-red-700',
};

const ITEMS_PER_PAGE = 20;

/**
 * LogsPage Component
 *
 * Admin page for viewing and filtering admin action logs.
 * Features:
 * - Paginated list of admin actions
 * - Filtering by admin, target user, action type, and date range
 * - Detailed view of action details
 *
 * @validates Requirements 12.7
 */
export function LogsPage() {
  const token = useStore((state) => state.token);

  // State
  const [logs, setLogs] = useState<AdminLogEntry[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<LogFilters>({
    adminId: '',
    targetUserId: '',
    actionType: '',
    startDate: '',
    endDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  /**
   * Fetch logs from the API
   */
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set('page', currentPage.toString());
      params.set('limit', ITEMS_PER_PAGE.toString());

      if (filters.adminId) params.set('adminId', filters.adminId);
      if (filters.targetUserId) params.set('targetUserId', filters.targetUserId);
      if (filters.actionType) params.set('actionType', filters.actionType);
      if (filters.startDate) params.set('startDate', new Date(filters.startDate).toISOString());
      if (filters.endDate) params.set('endDate', new Date(filters.endDate).toISOString());

      const response = await fetch(`/api/admin/logs?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data: LogsApiResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Erreur lors du chargement des logs');
      }

      setLogs(data.data);
      setPagination(data.pagination);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, filters]);

  // Fetch logs on mount and when dependencies change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (field: keyof LogFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({
      adminId: '',
      targetUserId: '',
      actionType: '',
      startDate: '',
      endDate: '',
    });
    setCurrentPage(1);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    if (isToday) {
      return `Aujourd'hui, ${timeStr}`;
    }
    if (isYesterday) {
      return `Hier, ${timeStr}`;
    }
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Format details object for display
   */
  const formatDetails = (details: Record<string, unknown>): string => {
    return JSON.stringify(details, null, 2);
  };

  /**
   * Toggle expanded log details
   */
  const toggleExpanded = (logId: string) => {
    setExpandedLogId((prev) => (prev === logId ? null : logId));
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters =
    filters.adminId || filters.targetUserId || filters.actionType || filters.startDate || filters.endDate;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Logs d'activité</h1>
          <p className="text-slate-500 text-sm mt-1">
            Historique des actions administratives
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter toggle button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all
              ${showFilters || hasActiveFilters
                ? 'bg-primary-100 text-primary-700 border border-primary-200'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }
            `}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="hidden sm:inline">Filtres</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
            )}
          </button>

          {/* Refresh button */}
          <button
            type="button"
            onClick={fetchLogs}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition-all disabled:opacity-50"
          >
            <svg
              className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Admin ID filter */}
            <div>
              <label htmlFor="filter-admin" className="block text-sm font-medium text-slate-700 mb-1">
                Admin ID
              </label>
              <input
                id="filter-admin"
                type="text"
                value={filters.adminId}
                onChange={(e) => handleFilterChange('adminId', e.target.value)}
                placeholder="ID de l'admin..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Target User ID filter */}
            <div>
              <label htmlFor="filter-target" className="block text-sm font-medium text-slate-700 mb-1">
                Utilisateur cible
              </label>
              <input
                id="filter-target"
                type="text"
                value={filters.targetUserId}
                onChange={(e) => handleFilterChange('targetUserId', e.target.value)}
                placeholder="ID de l'utilisateur..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Action type filter */}
            <div>
              <label htmlFor="filter-action" className="block text-sm font-medium text-slate-700 mb-1">
                Type d'action
              </label>
              <select
                id="filter-action"
                value={filters.actionType}
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
              >
                <option value="">Toutes les actions</option>
                {Object.entries(ACTION_TYPE_LABELS).map(([type, label]) => (
                  <option key={type} value={type}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Start date filter */}
            <div>
              <label htmlFor="filter-start-date" className="block text-sm font-medium text-slate-700 mb-1">
                Date début
              </label>
              <input
                id="filter-start-date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* End date filter */}
            <div>
              <label htmlFor="filter-end-date" className="block text-sm font-medium text-slate-700 mb-1">
                Date fin
              </label>
              <input
                id="filter-end-date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter actions */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {pagination?.total ?? 0} résultat(s) trouvé(s)
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Effacer les filtres
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Chargement des logs...</p>
        </div>
      )}

      {/* Logs table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {logs.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <p className="text-slate-500 font-medium">Aucun log trouvé</p>
              <p className="text-slate-400 text-sm mt-1">
                {hasActiveFilters
                  ? 'Essayez de modifier vos filtres'
                  : "Aucune action administrative n'a été enregistrée"}
              </p>
            </div>
          ) : (
            <>
              {/* Table for desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Admin</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Action</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Cible</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">IP</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Détails</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-900 truncate max-w-40">
                            {log.adminEmail || log.adminId}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                              ACTION_TYPE_STYLES[log.actionType] || 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {log.targetUserEmail || log.targetUserId || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                          {log.ipAddress || '—'}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(log.id)}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            {expandedLogId === log.id ? 'Masquer' : 'Voir'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Expanded details */}
                {expandedLogId && (
                  <div className="border-t border-slate-200 bg-slate-50 p-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      Détails de l'action
                    </h4>
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
                      {formatDetails(logs.find((l) => l.id === expandedLogId)?.details || {})}
                    </pre>
                  </div>
                )}
              </div>

              {/* Cards for mobile */}
              <div className="lg:hidden divide-y divide-slate-100">
                {logs.map((log) => (
                  <div key={log.id} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          ACTION_TYPE_STYLES[log.actionType] || 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                      </span>
                      <span className="text-xs text-slate-500">{formatDate(log.createdAt)}</span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Admin:</span>
                        <span className="text-slate-900 truncate max-w-48">
                          {log.adminEmail || log.adminId}
                        </span>
                      </div>
                      {(log.targetUserEmail || log.targetUserId) && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Cible:</span>
                          <span className="text-slate-900 truncate max-w-48">
                            {log.targetUserEmail || log.targetUserId}
                          </span>
                        </div>
                      )}
                      {log.ipAddress && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">IP:</span>
                          <span className="text-slate-900 font-mono text-xs">{log.ipAddress}</span>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleExpanded(log.id)}
                      className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      {expandedLogId === log.id ? 'Masquer les détails' : 'Voir les détails'}
                    </button>

                    {expandedLogId === log.id && (
                      <div className="mt-3">
                        <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs overflow-x-auto">
                          {formatDetails(log.details)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {pagination && pagination.total > ITEMS_PER_PAGE && (
            <div className="border-t border-slate-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-500">
                Affichage de {(currentPage - 1) * ITEMS_PER_PAGE + 1} à{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)} sur {pagination.total} entrées
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Précédent
                </button>

                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, Math.ceil(pagination.total / ITEMS_PER_PAGE)) },
                    (_, i) => {
                      const totalPages = Math.ceil(pagination.total / ITEMS_PER_PAGE);
                      let pageNum: number;

                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.hasMore}
                  className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LogsPage;
